import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Type,
  AlignLeft,
  Hash,
  CheckSquare,
  Heading1
} from 'lucide-react';
import { generateItemId, generateChoiceId } from '../../../../src/schemas/templateContent';

interface AddItemModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddItem: (item: any) => void;
}

const ITEM_TYPES = [
  {
    type: 'ynna_actions_review',
    label: 'Yes/No/N/A + Actions',
    description: 'Question with Y/N/N/A response options and actions field',
    icon: CheckSquare,
    color: 'text-green-600'
  },
  {
    type: 'dropdown',
    label: 'Dropdown',
    description: 'Question with custom dropdown options',
    icon: CheckSquare,
    color: 'text-teal-600'
  },
  {
    type: 'text',
    label: 'Text Input',
    description: 'Single line text field',
    icon: Type,
    color: 'text-purple-600'
  },
  {
    type: 'textarea',
    label: 'Text Area',
    description: 'Multi-line text field',
    icon: AlignLeft,
    color: 'text-orange-600'
  },
  {
    type: 'number',
    label: 'Number Input',
    description: 'Numeric input field',
    icon: Hash,
    color: 'text-red-600'
  },
  {
    type: 'section',
    label: 'Section Header',
    description: 'Visual divider to organize your template',
    icon: Heading1,
    color: 'text-blue-600'
  }
];

export function AddItemModal({ open, onOpenChange, onAddItem }: AddItemModalProps) {
  const createItem = (type: string) => {
    const baseItem = {
      id: generateItemId(),
      label: `New ${type.replace('_', ' ')} item`,
      type: type as any,
      actions_enabled: false
    };

    if (type === 'ynna_actions_review') {
      return {
        ...baseItem,
        actions_enabled: true,
        score: {
          enabled: true,
          weight: 1,
          map: {
            Yes: 1,
            No: 0,
            'N/A': 'ignore' as const
          }
        }
      };
    }

    if (type === 'dropdown') {
      return {
        ...baseItem,
        config: {
          choices: [
            { id: generateChoiceId(), label: 'Option 1', value: 'option_1' },
            { id: generateChoiceId(), label: 'Option 2', value: 'option_2' }
          ]
        },
        actions_enabled: true
        // No scoring for dropdown items
      };
    }

    return baseItem;
  };

  const handleSelectType = (type: string) => {
    const newItem = createItem(type);
    onAddItem(newItem);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="max-w-2xl"
        aria-labelledby="add-item-modal-title"
        aria-describedby="add-item-modal-description"
      >
        <DialogHeader>
          <DialogTitle id="add-item-modal-title">Add Item</DialogTitle>
          <DialogDescription id="add-item-modal-description">
            Choose an item type to add to your template
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-3 mt-4">
          {ITEM_TYPES.map((itemType) => {
            const Icon = itemType.icon;
            return (
              <Button
                key={itemType.type}
                variant="outline"
                className="h-auto p-4 justify-start hover:bg-accent"
                onClick={() => handleSelectType(itemType.type)}
                data-testid={`select-item-type-${itemType.type}`}
              >
                <div className="flex items-start gap-3 w-full">
                  <Icon className={`w-6 h-6 mt-0.5 ${itemType.color}`} />
                  <div className="text-left flex-1">
                    <div className="font-semibold text-base">{itemType.label}</div>
                    <div className="text-sm text-muted-foreground mt-1">
                      {itemType.description}
                    </div>
                  </div>
                </div>
              </Button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
